import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageSquare, Phone, Mail, Loader2 } from "lucide-react";
import { conversationsApi } from "../../services/api";

interface ClientMessagesTabProps {
  clientId: string;
  onCompose: () => void;
}

const ClientMessagesTab: React.FC<ClientMessagesTabProps> = ({ clientId, onCompose }) => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const res = await conversationsApi.list({ clientId });
        setConversations(res.data.data || res.data || []);
      } catch (err) {
        console.error("Failed to fetch conversations:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, [clientId]);

  const formatTimestamp = (date: string) => {
    if (!date) return "";
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHrs < 24) return `${diffHrs}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // Empty state
  if (conversations.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-16 text-center">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-brand-light mb-4">
          <MessageSquare className="h-7 w-7 text-brand" />
        </div>
        <p className="text-slate-800 font-semibold text-lg mb-1">No messages yet</p>
        <p className="text-slate-500 text-sm mb-6">Send a message to get started</p>
        <button
          onClick={onCompose}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
        >
          <MessageSquare className="h-4 w-4" />
          Send Message
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {conversations.map((conv: any, index: number) => {
        const channelIcon =
          conv.primaryChannel === "sms" || conv.primaryChannel === "phone" ? Phone : Mail;
        const ChannelIcon = channelIcon;
        const unread = conv.unreadCount || 0;

        return (
          <motion.div
            key={conv._id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            onClick={() => navigate("/communications")}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 cursor-pointer hover:shadow-md hover:border-slate-300 transition-all"
          >
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                <ChannelIcon className="h-5 w-5 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-slate-800 truncate">
                    {conv.subject || "Conversation"}
                  </h4>
                  <span className="text-xs text-slate-400 flex-shrink-0">
                    {formatTimestamp(conv.lastMessageAt || conv.updatedAt || conv.createdAt)}
                  </span>
                </div>
                <p className="text-sm text-slate-500 truncate mt-0.5">
                  {conv.lastMessagePreview || conv.lastMessage?.body || "No messages yet"}
                </p>
              </div>
              {unread > 0 && (
                <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-brand-accent text-white text-xs font-bold flex-shrink-0">
                  {unread}
                </span>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default ClientMessagesTab;
